/**
 * Verisoul Integration Utility
 *
 * This module handles Verisoul SDK initialization and provides utilities
 * for getting session IDs and reinitializing sessions.
 *
 * Environment variables required:
 * - VITE_VERISOUL_PROJECT_ID: Your Verisoul project ID
 * - VITE_VERISOUL_ENV: 'prod' or 'sandbox' (defaults to 'sandbox')
 */

/**
 * Initialize Verisoul SDK
 * Should be called early in the app lifecycle
 * Gracefully handles failures to prevent breaking the app
 */
export const initializeVerisoul = () => {
  try {
    const projectId = import.meta.env.VITE_VERISOUL_PROJECT_ID;
    const env = import.meta.env.VITE_VERISOUL_ENV || 'sandbox';

    // Only initialize if project ID is configured
    if (!projectId) {
      if (import.meta.env.DEV) {
        console.warn('Verisoul: VITE_VERISOUL_PROJECT_ID not configured. Verisoul will not be initialized.');
      }
      return;
    }

    // Check if already initialized
    if (window.Verisoul && window.__VERISOUL_INITIALIZED) {
      return;
    }

    // Create helper script to ensure Verisoul object is always available
    const helperScript = document.createElement('script');
    helperScript.textContent = `!function(e){if(e.Verisoul)return;const r=[],t={},o=new Proxy(t,{get:(e,o)=>o in t?t[o]:(...e)=>new Promise(((t,n)=>r.push([o,e,t,n]))),set:(e,r,o)=>(t[r]=o,!0)});e.Verisoul=o;const n=()=>{Object.keys(t).length&&r.splice(0).forEach((([e,r,o,n])=>{try{Promise.resolve(t[e](...r)).then(o,n)}catch(e){n(e)}}))},c=document.querySelector("script[verisoul-project-id]"),s=()=>r.splice(0).forEach((([,,,e])=>e(new Error("Failed to load Verisoul SDK"))));if(!c)return void s();c.addEventListener("load",n,{once:!0}),c.addEventListener("error",(()=>{clearInterval(i),s()}),{once:!0});const i=setInterval((()=>{Object.keys(t).length&&(clearInterval(i),n())}),40)}(window);`;
    document.head.appendChild(helperScript);

    // Load Verisoul SDK
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://js.verisoul.ai/${env}/bundle.js`;
    script.setAttribute('verisoul-project-id', projectId);
    script.onerror = () => {
      console.error('Verisoul: Failed to load SDK');
    };
    document.head.appendChild(script);

    window.__VERISOUL_INITIALIZED = true;
  } catch (error) {
    console.error('Verisoul: Failed to initialize', error);
    Sentry.captureException(error, {
      tags: {
        component: 'Verisoul',
        action: 'initializeVerisoul',
      },
    });
  }
};

/**
 * Get Verisoul session ID
 * Works for ALL users (authenticated and unauthenticated).
 * The session_id is tied to the browser session, not the user account.
 *
 * @returns {Promise<{session_id: string} | null>} Returns null if Verisoul is not available
 */
export const getVerisoulSession = async () => {
  if (!window.Verisoul) {
    return null;
  }

  try {
    const result = await window.Verisoul.session();
    return result;
  } catch (error) {
    // Return null instead of throwing to prevent breaking app functionality
    return null;
  }
};

/**
 * Reinitialize Verisoul session
 * Should be called when a user logs out
 * @returns {Promise<void>}
 */
export const reinitializeVerisoul = async () => {
  if (!window.Verisoul) {
    if (import.meta.env.DEV) {
      console.warn('Verisoul: SDK not initialized, skipping reinitialize');
    }
    return;
  }

  try {
    await window.Verisoul.reinitialize();
  } catch (error) {
    console.error('Verisoul: Failed to reinitialize', error);
    // Don't throw - logout should continue even if Verisoul fails
  }
};

/**
 * Check if Verisoul is available
 * @returns {boolean}
 */
export const isVerisoulAvailable = () => {
  return !!window.Verisoul;
};
